import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Breadcrumbs,
  Link,
  Skeleton,
  Tooltip,
  Typography,
  breadcrumbsClasses,
} from '@mui/material';

export interface BreadcrumbLink {
  label: string;
  href?: string;
  tip?: string;
}

interface Props {
  links: BreadcrumbLink[];
  isLoading: boolean;
  rightContent?: React.ReactNode;
}

export const BreadcrumbsV2: FC<Props> = ({ links, isLoading, rightContent }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
      <Breadcrumbs
      aria-label={t('breadcrumbs')}
      sx={{
        flexWrap: 'wrap',
        rowGap: 0.5,
        maxWidth: '100%',
        [`& .${breadcrumbsClasses.li}`]: { maxWidth: '100%' },
      }}
    >
      {links.map(({ href, tip, label }) => {
        if (isLoading) {
          return <Skeleton key={label} animation="wave" variant="text" width={80} />;
        }

        const labelElement = (
          <Typography
            component="span"
            aria-current={!href ? 'page' : undefined}
            aria-label={undefined}
            color="inherit"
            sx={{
              maxWidth: '100%',
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
            }}
          >
            {label}
          </Typography>
        );

        const contentElement = href ? (
          <Link
            href={href}
            underline="always"
            sx={(theme) => ({
              color: href ? theme.palette.primary.main : theme.palette.text.primary,
              maxWidth: '100%',
            })}
          >
            {labelElement}
          </Link>
        ) : (
          labelElement
        );

        const breadcrumbItem = tip ? (
          <Tooltip title={tip} arrow describeChild>
            {contentElement}
          </Tooltip>
        ) : (
          contentElement
        );

        return (
          <Box
            key={href || label}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            {breadcrumbItem}
          </Box>
        );
      })}
    </Breadcrumbs>
      {rightContent ?? null}
    </Box>
  );
};
